package com.upcominds.expedite.data.mobilelogs

import com.upcominds.expedite.data.NetworkClient
import com.upcominds.expedite.di.getKoin
import com.upcominds.expedite.dto.MobileLogCreateRequest
import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.koin.core.component.KoinComponent
import kotlin.random.Random

class MobileLogService(
    private val httpClient: HttpClient = NetworkClient.publicHttpClient
) : KoinComponent {
    private val runtimeInfo by lazy { AppRuntimeInfoProvider.current() }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val sessionId = buildSessionId()
    private val json = Json {
        encodeDefaults = true
        explicitNulls = true
    }

    suspend fun record(
        source: String,
        message: String,
        throwable: Throwable? = null,
        type: String = "ERROR"
    ) {
        if (!runtimeInfo.loggingEnabled) return

        val endpoint = NetworkClient.endpoint("api/mobile-logs")
        val payload = MobileLogCreateRequest(
            type = type,
            platform = runtimeInfo.platform,
            source = source,
            message = message.take(MAX_MESSAGE_LENGTH),
            stackTrace = throwable?.let(::formatThrowable)?.take(MAX_STACKTRACE_LENGTH),
            appVersion = runtimeInfo.appVersion,
            buildNumber = runtimeInfo.buildNumber,
            osVersion = runtimeInfo.osVersion,
            sessionId = sessionId,
            occurredAt = Clock.System.now().toString()
        )
        val rawPayload = json.encodeToString(payload)

        runCatching {
            val response = httpClient.post(endpoint) {
                method = HttpMethod.Post
                contentType(ContentType.Application.Json)
                headers.remove(HttpHeaders.Authorization)
                headers.remove(HttpHeaders.Accept)
                header(HttpHeaders.Accept, "*/*")
                header(HttpHeaders.UserAgent, "mobile/Ktor")
                setBody(rawPayload)
            }

            val responseBody = runCatching { response.bodyAsText() }.getOrDefault("")
            if (!response.status.isSuccess()) {
                error(
                    "Mobile log upload rejected with ${response.status.value}: " +
                        responseBody.ifBlank { response.status.description }
                )
            }

            println("MobileLogService: upload success to $endpoint with status ${response.status.value} body=$responseBody")
        }.onFailure {
            println(
                "MobileLogService: failed to upload log from $source to $endpoint " +
                    "platform=${payload.platform} type=${payload.type} session=${payload.sessionId} " +
                    "payload=$rawPayload: ${it.message}"
            )
        }
    }

    fun recordAsync(
        source: String,
        message: String,
        throwable: Throwable? = null,
        type: String = "ERROR"
    ) {
        if (!runtimeInfo.loggingEnabled) return

        scope.launch {
            record(
                source = source,
                message = message,
                throwable = throwable,
                type = type
            )
        }
    }

    private fun formatThrowable(throwable: Throwable): String = buildString {
        append(throwable::class.simpleName ?: "Throwable")
        throwable.message?.takeIf { it.isNotBlank() }?.let {
            append(": ")
            append(it)
        }

        var current = throwable.cause
        var depth = 0
        while (current != null && depth < 5) {
            append("\nCaused by: ")
            append(current::class.simpleName ?: "Throwable")
            current.message?.takeIf { it.isNotBlank() }?.let {
                append(": ")
                append(it)
            }
            current = current.cause
            depth += 1
        }
    }

    private fun buildSessionId(): String =
        "${Clock.System.now().toEpochMilliseconds()}-${Random.nextInt(1000, 9999)}"

    private companion object {
        const val MAX_MESSAGE_LENGTH = 2_000
        const val MAX_STACKTRACE_LENGTH = 12_000
    }
}

fun logMobileError(
    source: String,
    throwable: Throwable,
    message: String? = null
) {
    runCatching {
        getKoin().get<MobileLogService>()
    }.getOrNull()?.recordAsync(
        source = source,
        message = message ?: throwable.message ?: "Unexpected mobile error",
        throwable = throwable
    )
}
